import { createContext, useContext, useMemo } from 'react';
import { observer } from 'mobx-react';

import type {
  GetAllOpenQuestionAnswersResponse,
  GetSurveyResultResponse,
  GetUserQuestionAnswersRankingResponse,
} from '@genially/contracts';
import { type InteractiveQuestionsItemData } from '@genially/document-model';

import type {
  SidebarSummaryContextData,
  SidebarSummaryContextType,
} from '../../SidebarSummary.context';
import { useSidebarSummaryContext } from '../../SidebarSummary.context';

export type SelectedInteractiveQuestion<T = InteractiveQuestionsItemData> = T & {
  Id: string;
};

type FetchReturnType =
  | GetSurveyResultResponse
  | GetUserQuestionAnswersRankingResponse
  | GetAllOpenQuestionAnswersResponse;

interface SidebarSummaryWithContentContextType<T = SelectedInteractiveQuestion>
  extends Omit<SidebarSummaryContextType<T>, 'summary'> {
  summary: Omit<SidebarSummaryContextData, 'fetch' | 'lastUpdated'> & {
    data: FetchReturnType;
    lastUpdated: Date;
  };
}

const SidebarSummaryWithContentContext = createContext({});

interface SidebarSummaryContentProviderProps {
  children: React.ReactNode;
}

const SidebarSummaryContentProvider = observer(
  ({ children }: SidebarSummaryContentProviderProps) => {
    const { summary, ...summaryContext } = useSidebarSummaryContext();

    const context = useMemo((): SidebarSummaryWithContentContextType | undefined => {
      if (!summary.data || !summary.lastUpdated) return undefined;

      return {
        ...summaryContext,
        summary: {
          ...summary,
          lastUpdated: summary.lastUpdated,
          data: summary.data,
        },
      };
    }, [summary, summaryContext]);

    if (!context) {
      console.warn('SidebarSummaryWithContentContextProvider: No data available');
      return null;
    }

    return (
      <SidebarSummaryWithContentContext.Provider value={context}>
        {children}
      </SidebarSummaryWithContentContext.Provider>
    );
  },
);

const useSidebarSummaryWithContentContext = <T = InteractiveQuestionsItemData,>() => {
  const context = useContext(
    SidebarSummaryWithContentContext,
  ) as SidebarSummaryWithContentContextType<SelectedInteractiveQuestion<T>>;

  if (Object.keys(context).length === 0) {
    throw new Error(
      'useSidebarSummaryWithContentContext must be used within a SidebarSummaryContentProvider',
    );
  }

  return context;
};

export { SidebarSummaryContentProvider, useSidebarSummaryWithContentContext };
